import React, { useCallback, useRef } from 'react';
import useDoctorScheduleStore from './useDoctorScheduleStore';

function FilterSelect({ id, label, name, value, onChange, children }) {
  return (
    <div className="form-group select mb-0">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        name={name}
        className="form-control select2"
        value={value}
        onChange={onChange}
      >
        {children}
      </select>
      <i className="form-group__bar" />
    </div>
  );
}

function FilterBar({ translations }) {
  const filters = useDoctorScheduleStore((s) => s.filters);
  const filterOptions = useDoctorScheduleStore((s) => s.filterOptions);
  const pagination = useDoctorScheduleStore((s) => s.pagination);
  const viewMode = useDoctorScheduleStore((s) => s.viewMode);
  const setFilter = useDoctorScheduleStore((s) => s.setFilter);
  const setViewMode = useDoctorScheduleStore((s) => s.setViewMode);
  const fetchData = useDoctorScheduleStore((s) => s.fetchData);
  const t = translations.filters;
  const searchTimer = useRef(null);
  const dayOptions = [
    { value: 'Monday', label: t.days.monday },
    { value: 'Tuesday', label: t.days.tuesday },
    { value: 'Wednesday', label: t.days.wednesday },
    { value: 'Thursday', label: t.days.thursday },
    { value: 'Friday', label: t.days.friday },
    { value: 'Saturday', label: t.days.saturday },
    { value: 'Sunday', label: t.days.sunday },
  ];
  const sourceOptions = [
    { value: 'afya', label: t.source_synced },
    { value: 'internal', label: t.source_manual },
  ];

  const handleFilterChange = useCallback((key, value) => {
    setFilter(key, value);
    // fetchData triggered by useEffect in parent
  }, [setFilter]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setFilter('search', value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchData();
    }, 400);
  }, [setFilter, fetchData]);

  const handleSelectChange = useCallback((key) => (e) => {
    handleFilterChange(key, e.target.value);
  }, [handleFilterChange]);

  const handleToggle = useCallback(() => {
    setFilter('has_schedule_only', !filters.has_schedule_only);
  }, [filters.has_schedule_only, setFilter]);

  return (
    <div className="card doctor-schedule-mgmt__filter-panel mb-3">
      <div className="card-body">
        <div className="doctor-schedule-mgmt__filter-header">
          <div className="doctor-schedule-mgmt__filter-title">
            <i className="zmdi zmdi-filter-list" />
            <strong>{t.title}</strong>
          </div>
          <span className="doctor-schedule-mgmt__result-count">
            {pagination.total_count} {t.results}
          </span>
        </div>

        <div className="row doctor-schedule-mgmt__filter-fields align-items-end">
          <div className="col-12 col-lg-4 mb-3">
            <div className="form-group data-table__filter doctor-schedule-mgmt__search-filter mb-0">
              <label className="sr-only" htmlFor="doctor-schedule-search">{t.search_placeholder}</label>
              <input
                id="doctor-schedule-search"
                type="text"
                name="search"
                className="form-control"
                placeholder={t.search_placeholder}
                value={filters.search}
                onChange={handleSearchChange}
              />
              <i className="form-group__bar" />
            </div>
          </div>

          <div className="col-12 col-sm-6 col-lg-2 mb-3">
            <FilterSelect
              id="doctor-schedule-hospital"
              label={t.hospital_branch}
              name="hospital_id"
              value={filters.hospital_id}
              onChange={handleSelectChange('hospital_id')}
            >
                <option value="">{t.all_branches}</option>
                {(filterOptions.hospitals || []).map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
            </FilterSelect>
          </div>

          <div className="col-12 col-sm-6 col-lg-2 mb-3">
            <FilterSelect
              id="doctor-schedule-speciality"
              label={t.specialty}
              name="speciality_id"
              value={filters.speciality_id}
              onChange={handleSelectChange('speciality_id')}
            >
                <option value="">{t.all_specialties}</option>
                {(filterOptions.specialities || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
            </FilterSelect>
          </div>

          <div className="col-12 col-sm-6 col-lg-2 mb-3">
            <FilterSelect
              id="doctor-schedule-day"
              label={t.day}
              name="day"
              value={filters.day}
              onChange={handleSelectChange('day')}
            >
                <option value="">{t.all_days}</option>
                {dayOptions.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
            </FilterSelect>
          </div>

          <div className="col-12 col-sm-6 col-lg-2 mb-3">
            <FilterSelect
              id="doctor-schedule-source"
              label={t.source}
              name="source"
              value={filters.source}
              onChange={handleSelectChange('source')}
            >
                <option value="">{t.all_sources}</option>
                {sourceOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
            </FilterSelect>
          </div>
        </div>

        <div className="row">
          <div className="col-12 d-flex flex-wrap justify-content-lg-end justify-content-start">
            <div className="doctor-schedule-mgmt__filter-actions">
              <label className="doctor-schedule-mgmt__toggle-chip mb-0">
                <span className="toggle-switch toggle-switch--green doctor-schedule-mgmt__toggle-switch">
                  <input
                    type="checkbox"
                    className="toggle-switch__checkbox"
                    checked={filters.has_schedule_only}
                    onChange={handleToggle}
                  />
                  <i className="toggle-switch__helper" />
                </span>
                <span className="doctor-schedule-mgmt__toggle-copy">{t.has_schedule_only}</span>
              </label>

              <div className="doctor-schedule-mgmt__view-switch" role="group" aria-label={t.view_table}>
                <button
                  type="button"
                  className={`doctor-schedule-mgmt__view-btn ${viewMode === 'list' ? 'doctor-schedule-mgmt__view-btn--active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title={t.view_table}
                  aria-label={t.view_table}
                >
                  <i className="zmdi zmdi-view-list" />
                </button>
                <button
                  type="button"
                  className={`doctor-schedule-mgmt__view-btn ${viewMode === 'grid' ? 'doctor-schedule-mgmt__view-btn--active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title={t.view_cards}
                  aria-label={t.view_cards}
                >
                  <i className="zmdi zmdi-view-module" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilterBar;
